import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';
import type { Hours, ISODate, Timesheet, TimesheetStatus } from '@shared/types';

export interface TimesheetPeriod {
  cycle: string;
  startDate: ISODate;
  endDate: ISODate;
  dayCount: number;
  index: number;
  label: string;
}

export interface PeriodStatus {
  period: TimesheetPeriod;
  employeeId: string;
  timesheetId: string | null;
  state: TimesheetStatus | 'OPEN';
  totalHours: Hours;
  isClosed: boolean;
  dueDate: ISODate;
  isLate: boolean;
}

export interface GridCell {
  entryId: string;
  date: ISODate;
  hours: Hours;
  description: string | null;
  aiSuggested: boolean;
  invoiceLineId: string | null;
}

export interface GridRow {
  key: string;
  projectId: string | null;
  projectCode: string | null;
  projectName: string | null;
  clientName: string | null;
  taskCode: string | null;
  leaveType: string | null;
  isBillable: boolean;
  cells: GridCell[];
  total: Hours;
}

export interface WeekBlock {
  weekStart: ISODate;
  weekEnd: ISODate;
  days: ISODate[];
  total: Hours;
  workedHours: Hours;
}

export interface WeeklyGridView {
  timesheetId: string;
  employeeId: string;
  period: TimesheetPeriod;
  days: { date: ISODate; dayOfWeek: string; isWeekend: boolean }[];
  weeks: WeekBlock[];
  rows: GridRow[];
  dailyTotals: { date: ISODate; hours: Hours }[];
  total: Hours;
}

export interface TimesheetSettingsView {
  cycle: string;
  minIncrementHours: string;
  maxHoursPerDay: string;
  overtimeThresholdHours: string;
  billabilityMode: string;
  allowBillabilityOverride: boolean;
}

export interface TimesheetDetail {
  timesheet: Timesheet;
  entries: unknown[];
  settings: TimesheetSettingsView;
}

export interface OvertimeView {
  isOvertime: boolean;
  overtimeHours: Hours;
  thresholdHours: Hours;
  flsaStatus: string | null;
  overtimeEligible: boolean;
  weeks: { weekStart: ISODate; weekEnd: ISODate; workedHours: Hours; overtimeHours: Hours }[];
  classificationWarning?: string;
}

export interface SaveEntriesResponse {
  timesheet: Timesheet;
  entries: unknown[];
  warnings: { code?: string; message: string; field?: string }[];
  overtime: OvertimeView;
}

export interface EntryInput {
  id?: string | null;
  date: ISODate;
  projectId?: string | null;
  taskCode?: string | null;
  hours: string;
  description?: string | null;
  isBillable?: boolean | null;
  leaveType?: string | null;
  workType?: string | null;
}

export function useTimesheetPeriods(from?: ISODate, count = 6): UseQueryResult<{
  employeeId: string;
  cycle: string;
  periods: PeriodStatus[];
}> {
  return useQuery({
    queryKey: ['timesheets', 'periods', from ?? 'now', count],
    queryFn: () =>
      api.get<{ employeeId: string; cycle: string; periods: PeriodStatus[] }>('/timesheets/periods', {
        from,
        count,
      }),
  });
}

/** Opens (creating on first use) the sheet covering `date`. */
export function useOpenTimesheet(): ReturnType<
  typeof useMutation<{ timesheet: Timesheet; period: TimesheetPeriod; created: boolean }, unknown, { date?: ISODate; employeeId?: string }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { date?: ISODate; employeeId?: string }) =>
      api.post<{ timesheet: Timesheet; period: TimesheetPeriod; created: boolean }>('/timesheets/open', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export function useTimesheetGridQuery(timesheetId: string | null): UseQueryResult<WeeklyGridView> {
  return useQuery({
    queryKey: ['timesheets', 'grid', timesheetId],
    queryFn: () => api.get<WeeklyGridView>(`/timesheets/${timesheetId ?? ''}/grid`),
    enabled: timesheetId !== null && timesheetId !== '',
  });
}

export function useTimesheet(timesheetId: string | null): UseQueryResult<TimesheetDetail> {
  return useQuery({
    queryKey: ['timesheets', 'detail', timesheetId],
    queryFn: () => api.get<TimesheetDetail>(`/timesheets/${timesheetId ?? ''}`),
    enabled: timesheetId !== null && timesheetId !== '',
  });
}

export function useMyTimesheets(status?: TimesheetStatus): UseQueryResult<{ timesheets: Timesheet[] }> {
  return useQuery({
    queryKey: ['timesheets', 'mine', status ?? 'all'],
    queryFn: () => api.get<{ timesheets: Timesheet[] }>('/timesheets/mine', { status, limit: 26 }),
  });
}

export function usePendingTimesheets(): UseQueryResult<{ timesheets: Timesheet[]; scope: string }> {
  return useQuery({
    queryKey: ['timesheets', 'pending'],
    queryFn: () => api.get<{ timesheets: Timesheet[]; scope: string }>('/timesheets/pending'),
    retry: false,
  });
}

export function useSaveEntries(
  timesheetId: string | null,
): ReturnType<typeof useMutation<SaveEntriesResponse, unknown, { entries: EntryInput[]; mode?: 'REPLACE' | 'MERGE' }>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { entries: EntryInput[]; mode?: 'REPLACE' | 'MERGE' }) =>
      api.patch<SaveEntriesResponse>(`/timesheets/${timesheetId ?? ''}/entries`, input),
    onSuccess: (response) => {
      // The SERVER's timesheet (and therefore its status) replaces the cached one. The
      // grid never derives status locally — see TimesheetGrid for why that matters.
      queryClient.setQueryData<TimesheetDetail>(['timesheets', 'detail', timesheetId], (current) =>
        current === undefined ? current : { ...current, timesheet: response.timesheet },
      );
      void queryClient.invalidateQueries({ queryKey: ['timesheets', 'grid', timesheetId] });
      void queryClient.invalidateQueries({ queryKey: ['timesheets', 'periods'] });
    },
  });
}

export function useTimesheetAction(
  timesheetId: string | null,
): {
  submit: ReturnType<typeof useMutation<unknown, unknown, { notes?: string | null }>>;
  withdraw: ReturnType<typeof useMutation<unknown, unknown, void>>;
  copyPrevious: ReturnType<typeof useMutation<unknown, unknown, { includeHours: boolean }>>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    void queryClient.invalidateQueries({ queryKey: ['workflows'] });
  };

  return {
    submit: useMutation({
      mutationFn: (input: { notes?: string | null }) =>
        api.post(`/timesheets/${timesheetId ?? ''}/submit`, { notes: input.notes ?? null }),
      onSuccess: invalidate,
    }),
    withdraw: useMutation({
      mutationFn: () => api.post(`/timesheets/${timesheetId ?? ''}/withdraw`),
      onSuccess: invalidate,
    }),
    copyPrevious: useMutation({
      mutationFn: (input: { includeHours: boolean }) =>
        api.post(`/timesheets/${timesheetId ?? ''}/copy-previous`, input),
      onSuccess: invalidate,
    }),
  };
}

/**
 * The rest of what a person can do to their own week.
 *
 * `copy-previous` was wired and its two siblings were not, which is the wrong half to have
 * shipped. Copying last week is the convenience; the other two are the ones that prevent
 * wrong numbers:
 *
 *  · `fill-from-allocation` prefills from what the person is actually STAFFED to. Copying
 *    last week reproduces last week's project mix, so somebody who rolled onto a new
 *    engagement on Monday bills the old client all week unless they notice.
 *  · `recalculate` rebuilds the derived totals from the rows. Totals drift when an entry is
 *    edited through another path or a rate changes mid-period, and a total that no longer
 *    matches its rows is worse than no total, because it is what gets approved.
 *
 * Neither changes status, so neither is gated on anything more than `timesheets.edit`.
 */
export function useTimesheetPrefill(
  timesheetId: string | null,
): {
  fillFromAllocation: ReturnType<typeof useMutation<{ warnings?: string[] }, unknown, { includeWeekends: boolean }>>;
  recalculate: ReturnType<typeof useMutation<unknown, unknown, void>>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
  };

  return {
    fillFromAllocation: useMutation({
      mutationFn: (input: { includeWeekends: boolean }) =>
        api.post<{ warnings?: string[] }>(`/timesheets/${timesheetId ?? ''}/fill-from-allocation`, input),
      onSuccess: invalidate,
    }),
    recalculate: useMutation({
      // `{}` rather than nothing. A POST with no body carries no `Content-Type`, and the
      // JSON-only guard rejects it 415 before the handler runs — so the button reported
      // "This endpoint accepts JSON only" to a user who had sent nothing at all.
      mutationFn: () => api.post(`/timesheets/${timesheetId ?? ''}/recalculate`, {}),
      onSuccess: invalidate,
    }),
  };
}

/**
 * Submit every editable sheet for a period that has already ended.
 *
 * Somebody back from three weeks of leave has three sheets to submit and no reason to open
 * each one. The server decides what "editable" and "ended" mean — this never guesses which
 * sheets qualify, because a client-side filter that disagrees with the server submits
 * nothing and reports success.
 */
export function useSubmitAllTimesheets(): ReturnType<
  typeof useMutation<{ submitted?: string[]; skipped?: { timesheetId: string; reason: string }[] }, unknown, void>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ submitted?: string[]; skipped?: { timesheetId: string; reason: string }[] }>(
        '/timesheets/submit-all',
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

/**
 * The payroll lock — the boundary between "hours somebody typed" and "hours that were paid".
 *
 * Locking makes the sheet an immutable input to payroll and billing. Unlocking is the
 * sensitive half: it carries a reason, is audited at HIGH (CRITICAL once the hours have been
 * invoiced or paid), and may route to an approval workflow rather than taking effect
 * immediately. The caller must therefore read `route` — reporting "unlocked" when the server
 * said "sent for approval" tells payroll they can edit a sheet that is still locked.
 */
export interface LockPeriodResult {
  locked?: string[];
  skipped?: { timesheetId: string; employeeName?: string; reason?: string }[];
  message?: string;
}

export interface UnlockResult {
  route?: 'DIRECT' | 'WORKFLOW';
  message?: string;
  warnings?: string[];
}

export interface PayrollTimesheetLine {
  timesheetId: string;
  periodStart: ISODate;
  periodEnd: ISODate;
  status: TimesheetStatus;
  isLocked: boolean;
  totalHours: string;
  overtimeHours: string;
  billableHours: string;
  approvedAt: string | null;
  submittedWithExpiredWorkAuth?: boolean;
}

export interface PayrollHoursRow {
  employeeId: string;
  employeeName?: string | null;
  timesheets: PayrollTimesheetLine[];
  totalHours: string;
  overtimeHours: string;
  billableHours: string;
  /** A UNION, never a number. When it is not evaluable the reason names the missing record. */
  overtimeRate: { evaluable?: boolean; reason?: string; value?: string | null } | null;
  allLocked: boolean;
  anyExpiredWorkAuth: boolean;
}

/** What payroll is about to consume. Never mutates — preview as often as you like. */
export function usePayrollHours(
  periodStart: string,
  periodEnd: string,
): UseQueryResult<{ rows: PayrollHoursRow[] }> {
  return useQuery({
    queryKey: ['timesheets', 'payroll-hours', periodStart, periodEnd],
    queryFn: () =>
      api.get<{ rows: PayrollHoursRow[] }>('/timesheets/payroll/hours', {
        periodStart,
        periodEnd,
        includeUnlocked: true,
      }),
    enabled: periodStart !== '' && periodEnd !== '',
    retry: false,
  });
}

export function useTimesheetLock(): {
  lockPeriod: ReturnType<
    typeof useMutation<LockPeriodResult, unknown, { periodStart: string; periodEnd: string }>
  >;
  lockOne: ReturnType<typeof useMutation<{ message?: string }, unknown, { id: string }>>;
  unlockOne: ReturnType<typeof useMutation<UnlockResult, unknown, { id: string; reason: string }>>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    void queryClient.invalidateQueries({ queryKey: ['payroll'] });
    void queryClient.invalidateQueries({ queryKey: ['workflows'] });
  };

  return {
    lockPeriod: useMutation({
      mutationFn: (input: { periodStart: string; periodEnd: string }) =>
        api.post<LockPeriodResult>('/timesheets/lock-period', input),
      onSuccess: invalidate,
    }),
    lockOne: useMutation({
      mutationFn: (input: { id: string }) =>
        api.post<{ message?: string }>(`/timesheets/${input.id}/lock`, {}),
      onSuccess: invalidate,
    }),
    unlockOne: useMutation({
      mutationFn: (input: { id: string; reason: string }) =>
        api.post<UnlockResult>(`/timesheets/${input.id}/unlock`, { reason: input.reason }),
      onSuccess: invalidate,
    }),
  };
}

export function useApproveTimesheet(): {
  approve: ReturnType<typeof useMutation<unknown, unknown, { id: string; comment?: string | null }>>;
  reject: ReturnType<typeof useMutation<unknown, unknown, { id: string; reason: string }>>;
  bulkApprove: ReturnType<
    typeof useMutation<
      { approved?: string[]; refused?: { timesheetId: string; message: string }[] },
      unknown,
      { timesheetIds: string[]; comment?: string | null }
    >
  >;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    void queryClient.invalidateQueries({ queryKey: ['workflows'] });
  };

  return {
    approve: useMutation({
      mutationFn: (input: { id: string; comment?: string | null }) =>
        api.post(`/timesheets/${input.id}/approve`, { comment: input.comment ?? null }),
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: (input: { id: string; reason: string }) =>
        api.post(`/timesheets/${input.id}/reject`, { reason: input.reason }),
      onSuccess: invalidate,
    }),
    bulkApprove: useMutation({
      mutationFn: (input: { timesheetIds: string[]; comment?: string | null }) =>
        api.post<{ approved?: string[]; refused?: { timesheetId: string; message: string }[] }>(
          '/timesheets/bulk-approve',
          { timesheetIds: input.timesheetIds, comment: input.comment ?? null },
        ),
      onSuccess: invalidate,
    }),
  };
}

export interface BookableProject {
  projectId: string;
  code: string;
  name: string;
  clientName: string;
  isBillable?: boolean;
  taskCodes?: string[];
  billRate?: string | null;
}

export function useBookableProjects(date?: ISODate): UseQueryResult<{ projects: BookableProject[] }> {
  return useQuery({
    queryKey: ['projects', 'bookable', date ?? 'today'],
    queryFn: () => api.get<{ projects: BookableProject[] }>('/projects/bookable', { date }),
    staleTime: 5 * 60_000,
  });
}
